import { Slide } from '@features/powerpoint';
import { DEFAULT_SLIDE_MASTER } from '../../../slides/slide-master';
import { formatters } from '@utils/formattersV2';
import type { RslDimension } from '@features/campaigns/subdomains/rsl';
import { RslReport } from '@features/campaigns/subdomains/rsl';
import { texts } from '../../../slides/sales-dimensions/texts';
import { shapes } from '../../../slides/sales-dimensions/shapes';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { FormSchema } from '../form-schema';
import { images } from '../../../slides/sales-dimensions/images';
import { visibleSlide } from '@features/executive-report/slides';
import { headerTabs, TabId } from './header-tabs';
import { SalesDimensionsSlideBaseOptions } from '@features/executive-report/slides/sales-dimensions/content';
import { theme } from '@features/executive-report/domain/theme';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import {
  OVERALL_LINE_ITEM_OFFSET,
  OverallLineItemTextsOptions,
} from '../../../slides/sales-dimensions/texts';
import { topSignificantDimensions } from '../domain/significant-dimensions';
import { RSL_DIMENSION_SLUGS } from '../constants/dimensions';

type TabIdOptions = {
  activeHeaderTab: TabId;
};

type SalesDimensionsSlideOptions = SalesDimensionsSlideBaseOptions &
  TabIdOptions;

const overallLineItemTexts = (
  options: OverallLineItemTextsOptions
): NonNullable<Slide['texts']> => [
  {
    text: options.label.toUpperCase(),
    options: {
      fontSize: 9,
      color: theme.colors.white,
      h: 0.25,
      w: 2.71,
      x: 0.94,
      y: 3.54 + OVERALL_LINE_ITEM_OFFSET * options.index,
    },
  },
  {
    text: options.formattedValue,
    options: {
      fontSize: 20,
      color: theme.colors.white,
      bold: true,
      h: 0.44,
      w: 1.43,
      x: 0.94,
      y: 3.72 + OVERALL_LINE_ITEM_OFFSET * options.index,
    },
  },
  {
    text: options.liftFormatter
      ? formatWithSign(options.lift, options.liftFormatter)
      : '',
    options: {
      fontSize: 20,
      color: theme.colors.inmarketGreen,
      h: 0.44,
      w: 1.45,
      x: 2.36,
      y: 3.72 + OVERALL_LINE_ITEM_OFFSET * options.index,
    },
  },
];

const salesDimensionsSlide = (
  options: SalesDimensionsSlideOptions
): Slide[] => {
  return visibleSlide({
    slide: {
      slideMaster: DEFAULT_SLIDE_MASTER,
      texts: texts(
        options,
        headerTabs(options.activeHeaderTab),
        overallLineItemTexts
      ),
      shapes: shapes(options.subdimensions),
      images: images(options.subdimensions),
    },
    visible: options.visible,
  });
};

const dimensionToSalesDimensionOptions =
  (subdimensionImages: FormSchema['subdimensionImagesByNameAndValue']) =>
  (
    dimension: RslDimension
  ): SalesDimensionsSlideOptions['subdimensions'][number] => {
    return {
      name: dimension.dimensionValue,
      incrementalSalesLift: dimension.incrementalSalesLift,
      incrementalSales: dimension.projectedIncrementalSales,
      impressions: dimension.impressions,
      image:
        subdimensionImages[dimension.dimensionName]?.[
          dimension.dimensionValue
        ] ?? null,
    };
  };

const liftPartFormatter: OverallLineItemTextsOptions['liftFormatter'] = value =>
  formatters.percentage(value, { decimals: 1 });

type SalesDimensionsSlidesOptions = {
  report: RslReport;
  recommendations: FormSchema['salesDimensionRecommendations'];
  includedSlides: FormSchema['includedSlides']['sales'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
};

export const salesDimensionsSlides = (
  options: SalesDimensionsSlidesOptions
): Slide[] => {
  const overallMetricsSales: SalesDimensionsSlideOptions['overallMetrics'] = [
    {
      label: 'Incremental Sales and Lift',
      formattedValue: formatters.currency(
        options.report.projectedIncrementalSales,
        { decimals: 1 }
      ),
      lift: options.report.incrementalSalesLift ?? 0,
      liftFormatter: formatters.lift,
    },
    {
      label: 'Transaction Size and Lift',
      formattedValue: formatters.currency(options.report.transactionSize, {
        decimals: 0,
      }),
      lift: options.report.transactionSizeLift ?? 0,
      liftFormatter: liftPartFormatter,
    },
    {
      label: 'Penetration and Lift',
      formattedValue: formatters.percentage(options.report.penetration, {
        decimals: 1,
      }),
      lift: options.report.penetrationLift ?? 0,
      liftFormatter: liftPartFormatter,
    },
    {
      label: 'Transaction Rate and Lift',
      formattedValue: formatters.number(options.report.transactionRate, {
        decimals: 1,
      }),
      lift: options.report.transactionRateLift ?? 0,
      liftFormatter: liftPartFormatter,
    },
  ];

  const [audSlug, creativeSlug, channelSlug] = RSL_DIMENSION_SLUGS;

  const audDimensions = topSignificantDimensions(
    options.report.dimensions,
    audSlug
  );
  const creativeDimensions = topSignificantDimensions(
    options.report.dimensions,
    creativeSlug
  );
  const channelDimensions = topSignificantDimensions(
    options.report.dimensions,
    channelSlug
  );

  const salesDimensionsSlideOptions: SalesDimensionsSlideOptions[] = [
    ...(audDimensions.length > 0
      ? ([
          {
            dimensionName: apiSlugToDimensionName.aud,
            activeHeaderTab: 'who',
            subdimensions: audDimensions.map((d: RslDimension) =>
              dimensionToSalesDimensionOptions(options.subdimensionImages)(d)
            ),
            recommendation: options.recommendations.who,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHO: </lci><b>Audiences</b>',
            visible: options.includedSlides.who,
          },
        ] as const)
      : []),
    ...(creativeDimensions.length > 0
      ? ([
          {
            dimensionName: apiSlugToDimensionName.creative,
            activeHeaderTab: 'what',
            subdimensions: creativeDimensions.map((d: RslDimension) =>
              dimensionToSalesDimensionOptions(options.subdimensionImages)(d)
            ),
            recommendation: options.recommendations.what,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHAT: </lci><b>Creatives</b>',
            visible: options.includedSlides.what,
          },
        ] as const)
      : []),
    ...(channelDimensions.length > 0
      ? ([
          {
            dimensionName: apiSlugToDimensionName.channel,
            activeHeaderTab: 'where',
            subdimensions: channelDimensions.map((d: RslDimension) =>
              dimensionToSalesDimensionOptions(options.subdimensionImages)(d)
            ),
            recommendation: options.recommendations.where,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHERE: </lci><b>Channels</b>',
            visible: options.includedSlides.where,
          },
        ] as const)
      : []),
  ];

  return salesDimensionsSlideOptions.flatMap(options =>
    salesDimensionsSlide(options)
  );
};
